import type React from 'react';
import { lazy, Suspense, useState } from 'react';
import type { PollRead } from '@models/api/poll';
import { EndPollDrawer } from './EndPollDrawer';
import { PollVotersDrawer } from './PollVotersDrawer';

// Lazy on purpose: Reopen holds the day picker, and every feed card calls this hook, so a static import
// would load react-day-picker for every visitor. Few people ever open this drawer.
const ReopenPollDrawer = lazy(() =>
  import('./ReopenPollDrawer').then(m => ({ default: m.ReopenPollDrawer })),
);

export interface PollActionQuestion {
  id: string;
  slug: string | null;
  title: string;
  is_my_question: boolean;
}

/**
 * The drawers a poll opens from its card (TECH-197): "Who answered" from See who (B6), End poll
 * now's confirm (E3) and Reopen (E14). One place for the feed card and the old post page, so both
 * open the same ones. `drawers` goes outside the card: a tap inside a portalled drawer bubbles
 * through the React tree, and the card's own click would open the Post sheet.
 */
export const usePollActionDrawers = (
  question: PollActionQuestion | null,
  poll: PollRead | null,
): {
  openVoters: () => void;
  openEnd: () => void;
  openReopen: () => void;
  drawers: React.ReactNode;
} => {
  const [open, setOpen] = useState<'voters' | 'end' | 'reopen' | null>(null);
  // Set on the first opening and left set, so the drawer stays mounted to play its closing animation.
  const [reopenMounted, setReopenMounted] = useState(false);
  // Names stopped being the viewer's to see while "Who answered" was open (a re-read): it closes for good, so it
  // never reopens by itself if they come back. Adjusting state during render, the pattern React documents.
  if (open === 'voters' && poll?.can_see_voters === false) setOpen(null);
  const close = (next: boolean) => {
    if (!next) setOpen(null);
  };

  const drawers =
    poll && question ? (
      <>
        <PollVotersDrawer
          // Closes if names stop being the viewer's to see (the poll was re-read meanwhile).
          open={open === 'voters' && poll.can_see_voters}
          onOpenChange={close}
          questionId={question.id}
          question={question.title}
          options={poll.options}
          canSeeVoters={poll.can_see_voters}
        />
        {reopenMounted ? (
          <Suspense fallback={null}>
            <ReopenPollDrawer
              open={open === 'reopen'}
              onOpenChange={close}
              question={question}
              poll={poll}
            />
          </Suspense>
        ) : null}
        <EndPollDrawer open={open === 'end'} onOpenChange={close} question={question} poll={poll} />
      </>
    ) : null;

  return {
    openVoters: () => {
      setOpen('voters');
    },
    openEnd: () => {
      setOpen('end');
    },
    openReopen: () => {
      setReopenMounted(true);
      setOpen('reopen');
    },
    drawers,
  };
};
